// src/components/PrevNextPager.jsx
//
// DETAIL-PREVNEXT-PAGER: Prev / Next pager for Office detail and Lease detail
// sub-nav rows. Walks the exact ordered/filtered sequence passed from the source
// list via React Router navigation state.
//
// Props:
//   ids        string[]   — ordered list of IDs from the source list
//   currentId  string     — the ID of the currently-displayed record
//   onNavigate function   — called with the target ID when the user navigates
//   variant    "hairline" (default, unchanged) | "icon" (Task #9099 shared
//              record header: 30px chevron tools with tooltips)
//   showPosition boolean  — icon variant: truthful "{n} of {total}"; a record
//              outside the list shows "Not in list", never an invented position.

import React, { useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { T } from "../app/tokens.js";
import { HairlineNavButton } from "./HairlineNavButton.jsx";

// Ownership belongs to the last active mounted pager, even at a boundary.
// Consumers underneath a drawer explicitly suspend their ownership.
const keyboardOwners = new Set();
const KEY_WIDGET = 'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="tablist"], [role="tab"], [role="listbox"], [role="menu"], [role="combobox"], [role="slider"], [role="spinbutton"], [role="tree"], [role="grid"], [data-record-nav-ignore]';
function visible(el) {
  return !el.closest('[hidden], [aria-hidden="true"], [inert]') &&
    getComputedStyle(el).display !== "none" && getComputedStyle(el).visibility !== "hidden";
}

export function recordArrowBlocked(event, owner) {
  if (event.defaultPrevented || event.isComposing || event.keyCode === 229 ||
      event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return true;
  if ([event.target, document.activeElement].some(el => el?.closest?.(KEY_WIDGET))) return true;
  if ([...document.querySelectorAll('[aria-haspopup]:not([aria-haspopup="false"])[aria-expanded="true"], [role="menu"], [role="listbox"]')].some(visible)) return true;
  const dialog = owner?.closest('[role="dialog"], [role="alertdialog"]');
  return [...document.querySelectorAll('[role="dialog"], [role="alertdialog"], dialog[open]')]
    .some(el => visible(el) && !el.hasAttribute("data-media-transient-confirmation") && el !== dialog && !el.contains(owner));
}

/** Pure: truthful position text, or null when the record is not in the list. */
export function pagerPosition(ids, currentId) {
  const list = Array.isArray(ids) ? ids : [];
  const idx = list.indexOf(currentId);
  return idx === -1 ? null : `${idx + 1} of ${list.length}`;
}

export function PrevNextPager({
  ids, currentId, onNavigate, disabled = false, busy = false, keyboardActive = true,
  compact = false, showUnavailable = false, previousLabel = "Previous", nextLabel = "Next",
  previousTestId = "prevnext-prev-btn", nextTestId = "prevnext-next-btn",
  variant = "hairline", showPosition = false, positionTestId = "prevnext-position",
  verticalKeys = false,
}) {
  const root = useRef(null);
  const owner = useRef({});
  const list = Array.isArray(ids) ? ids : [];
  const idx = list.indexOf(currentId);

  const hasPrev = !disabled && !busy && idx > 0;
  const hasNext = !disabled && !busy && idx >= 0 && idx < list.length - 1;
  const prevId  = hasPrev ? list[idx - 1] : null;
  const nextId  = hasNext ? list[idx + 1] : null;
  const rendered = showUnavailable || (list.length > 0 && idx !== -1);
  const current = useRef(null);
  current.current = { prevId, nextId, onNavigate };
  const navigate = (direction) => {
    const state = current.current;
    const id = direction === "prev" ? state.prevId : state.nextId;
    if (id != null) state.onNavigate(id);
  };

  useEffect(() => {
    if (!keyboardActive || !rendered) return undefined;
    const token = owner.current;
    keyboardOwners.add(token);
    function handleKey(e) {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight" &&
          !(verticalKeys && (e.key === "ArrowUp" || e.key === "ArrowDown"))) return;
      if ([...keyboardOwners].at(-1) !== token || recordArrowBlocked(e, root.current)) return;
      const state = current.current;
      const id = e.key === "ArrowLeft" || e.key === "ArrowUp" ? state.prevId : state.nextId;
      if (id != null) { e.preventDefault(); state.onNavigate(id); }
    }
    window.addEventListener("keydown", handleKey);
    return () => { keyboardOwners.delete(token); window.removeEventListener("keydown", handleKey); };
  }, [keyboardActive, rendered, verticalKeys]);

  if (!rendered) return null;

  if (variant === "icon") {
    const pos = pagerPosition(list, currentId);
    return (
      <div ref={root} data-testid="prevnext-pager" style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
        <button type="button" className="rdh-tool" data-chrome-action="pager" data-testid={previousTestId} data-tip={previousLabel}
          aria-label={previousLabel} disabled={!hasPrev} onClick={() => navigate("prev")}>
          <ChevronLeft size={16} aria-hidden="true" />
        </button>
        {showPosition && (
          <span data-testid={positionTestId} aria-live="polite"
            style={{ minWidth: 52, textAlign: "center", fontSize: T.fsXs, fontWeight: T.fwSemibold, color: pos ? T.dark : T.mid, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
            {pos ?? "Not in list"}
          </span>
        )}
        <button type="button" className="rdh-tool" data-chrome-action="pager" data-testid={nextTestId} data-tip={nextLabel}
          aria-label={nextLabel} disabled={!hasNext} onClick={() => navigate("next")}>
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div
      ref={root}
      data-testid="prevnext-pager"
      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
    >
      <HairlineNavButton compact={compact} label="‹ Prev" ariaLabel={previousLabel} testId={previousTestId}
        disabled={!hasPrev} onClick={() => navigate("prev")} />
      <HairlineNavButton compact={compact} label="Next ›" ariaLabel={nextLabel} testId={nextTestId}
        disabled={!hasNext} onClick={() => navigate("next")} />
    </div>
  );
}
